import { Tree } from '@blueprintjs/core';
import { observer } from 'mobx-react';
import React, { useMemo, useState } from 'react';
import { TbDashboard } from 'react-icons/tb';
import styled from 'styled-components';
import { FlexColumn } from 'core/components';
import $decks from 'app/stores/decks/$decks';
import { InputGroup } from 'core/form';
import SidebarSection from 'app/views/boards/SidebarSection';
import { defaultsDeep, pick } from 'lodash';
import { getWidgetFields, getWidgetLayout } from './utils';

const StyledTree = styled(Tree)`
  // give the nodes a little more breathing room
  .bp4-tree-node-content {
    height: 26px;
    border-radius: 8px;
  }

  // visually distinguish categories from widgets
  .widget-tree-category > .bp4-tree-node-content {
    font-weight: 600;
    height: 32px;
  }

  .widget-tree-leaf {
    .bp4-tree-node-content-1 {
      padding-left: 8px;
    }
  }
`;

function getWidgetTree(widgets) {
  const tree = new Map();

  widgets.forEach((widget) => {
    const category = widget.category || 'Uncategorized';
    if (!tree.has(category)) {
      tree.set(category, { id: category, label: category, childNodes: [] });
    }
    tree.get(category).childNodes.push({
      id: widget.name,
      category,
      label: widget.title,
      className: 'widget-tree-leaf'
    });
  });

  tree.forEach((node) => {
    node.className = 'widget-tree-category';
    node.childNodes.sort((a, b) => a.label.localeCompare(b.label));
  });

  return Array.from(tree.values()).sort((a, b) => a.label.localeCompare(b.label));
}

function WidgetPanelForm(props) {
  const { form, panel } = props;
  const [filter, setFilter] = useState('');
  const [isExpanded, setExpanded] = useState({});
  const widgetNameField = form.getField('config.widgetName');
  const widgetName = widgetNameField.value;

  const baseWidgetTree = useMemo(() => {
    const widgets = $decks.getWidgets(filter);
    return getWidgetTree(widgets);
  }, [filter]);

  const widgetTree = baseWidgetTree.map((node) => ({
    ...node,
    isExpanded: isExpanded[node.id] ?? true,
    childNodes: node.childNodes.map((child) => ({
      ...child,
      isSelected: child.id === widgetName
    }))
  }));

  const handleFilter = (evt) => {
    setFilter(evt.target.value);
  };

  const handleWidgetChange = (node) => {
    widgetNameField.setValue(node.id);

    const fields = getWidgetFields(node.id);
    const layout = getWidgetLayout(node.id);
    if (fields) {
      form.addFields(fields);
    }
    if (layout) {
      const currentPosition = pick(panel.get('layout'), ['sm.x', 'sm.y', 'lg.x', 'lg.y']);
      const mergedLayout = defaultsDeep(currentPosition, layout);
      panel.set('layout', mergedLayout);
    }
  };

  const handleNodeClick = (node) => {
    if (node.childNodes?.length) {
      setExpanded((prev) => ({ ...prev, [node.id]: !prev[node.id] }));
    } else {
      handleWidgetChange(node);
    }
  };

  const handleNodeCollapse = (node) => {
    setExpanded((prev) => ({ ...prev, [node.id]: false }));
  };

  const handleNodeExpand = (node) => {
    setExpanded((prev) => ({ ...prev, [node.id]: true }));
  };

  return (
    <SidebarSection title="Widget Type" icon={TbDashboard} defaultIsOpen isCollapsible={false}>
      <FlexColumn gap={1} pt="2px" px={1}>
        <InputGroup
          type="search"
          fill
          leftIcon="search"
          value={filter}
          placeholder="Search widgets..."
          onChange={handleFilter}
        />
        <StyledTree
          contents={widgetTree}
          onNodeClick={handleNodeClick}
          onNodeCollapse={handleNodeCollapse}
          onNodeExpand={handleNodeExpand}
        />
      </FlexColumn>
    </SidebarSection>
  );
}

export default observer(WidgetPanelForm);
